import { ChangeEvent, useContext, useEffect, useRef, useState } from "react";
import { Box, Button, CircularProgress, Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import dayjs from "dayjs";
import axios from "axios";
import lottie, { type AnimationItem } from "lottie-web";

import { AuthContext } from "../../../contexts/AuthContext";
import { CONFIG } from "../../../services/config";

import { FormInputDate } from "../../../components/form/FormInputDate";
import { FormInputText } from "../../../components/form/FormInputText";
import ActionButtons from "../../../components/MyModal/ActionButtons";
import MyModal from "../../../components/MyModal";

type Props = {
  onClose: () => void;
  openSuccessAlert?: (msg?: string) => void;
  openErrorAlert?: (msg?: string) => void;
  refreshPage?: () => void;
};

interface FormValues {
  name: string;
  startDate: string;
  endDate: string;
  mediaFile: File | null;
}

const styles = {
  sectionLabel: { fontSize: 16 },
  subLabel: { fontSize: 12 },
  alignCenter: { display: "flex", alignItems: "center" },
  imageContent: { width: 48, height: 86, marginLeft: "16px" },
  mediaPreview: {
    width: "100%",
    height: "100%",
    objectFit: "cover" as const,
  },
  lottiePreview: {
    width: "100%",
    height: "100%",
    overflow: "hidden" as const,
  },
};

const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5 MB — IMAGE/WEBP
const MAX_VIDEO_SIZE = 10 * 1024 * 1024; // 10 MB
const TARGET_ASPECT_RATIO = 9 / 16;
const ASPECT_RATIO_TOLERANCE = 0.02;

export default function ModalAddSplashScreen({
  onClose,
  openSuccessAlert,
  openErrorAlert,
  refreshPage,
}: Props) {
  const { t } = useTranslation();
  const { tokenHeaders } = useContext(AuthContext);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const lottieContainerRef = useRef<HTMLDivElement>(null);
  const lottieAnimationRef = useRef<AnimationItem | null>(null);
  const [mediaPreview, setMediaPreview] = useState<string | null>(null);
  const [videoPreview, setVideoPreview] = useState<string | null>(null);
  const [isLottiePreview, setIsLottiePreview] = useState(false);
  const [loading, setLoading] = useState(false);


  const schema = yup.object().shape({
    name: yup
      .string()
      .required(t("splashScreen.add.validation.nameRequired"))
      .test(
        "no-only-spaces",
        t("splashScreen.add.validation.nameRequired"),
        (value) => !value || value.trim().length > 0,
      ),
    startDate: yup
      .string()
      .required(t("splashScreen.add.validation.startDateRequired"))
      .test(
        "start-before-end",
        t("splashScreen.add.validation.endDateGreaterThanStart"),
        (value, ctx) => {
          const { endDate } = ctx.parent as FormValues;
          if (!value || !endDate) return true;
          return value <= endDate;
        },
      ),
    endDate: yup
      .string()
      .required(t("splashScreen.add.validation.endDateRequired"))
      .test(
        "end-after-start",
        t("splashScreen.add.validation.endDateGreaterThanStart"),
        (value, ctx) => {
          const { startDate } = ctx.parent as FormValues;
          if (!startDate || !value) return true;
          return value > startDate;
        },
      ),
    mediaFile: yup
      .mixed<File>()
      .nullable()
      .required(t("splashScreen.add.validation.fileRequired"))
      .test(
        "file-required",
        t("splashScreen.add.validation.fileRequired"),
        (value) => value instanceof File,
      ),
  });

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    setError,
    formState: { errors, isValid, isSubmitting },
  } = useForm<FormValues>({
    resolver: yupResolver(schema) as any,
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      name: "",
      startDate: "",
      endDate: "",
      mediaFile: null,
    },
  });

  const watchedName = watch("name");
  const watchedStartDate = watch("startDate");
  const watchedEndDate = watch("endDate");
  const watchedMediaFile = watch("mediaFile");
  const showExpandedFields =
    watchedName?.trim().length > 0 ||
    !!watchedStartDate ||
    !!watchedEndDate ||
    !!watchedMediaFile;

  const isValidVerticalRatio = (width: number, height: number) => {
    if (!width || !height) return false;
    const ratio = width / height;
    return Math.abs(ratio - TARGET_ASPECT_RATIO) <= ASPECT_RATIO_TOLERANCE;
  };

  const destroyLottiePreview = () => {
    lottieAnimationRef.current?.destroy();
    lottieAnimationRef.current = null;

    if (lottieContainerRef.current) {
      lottieContainerRef.current.innerHTML = "";
    }
  };

  const clearPreviews = () => {
    setMediaPreview(null);
    setVideoPreview(null);
    setIsLottiePreview(false);
    destroyLottiePreview();
  };

  const renderLottiePreview = (animationData: Record<string, unknown>) => {
    if (!lottieContainerRef.current) {
      throw new Error("Lottie container not found");
    }

    destroyLottiePreview();

    lottieAnimationRef.current = lottie.loadAnimation({
      container: lottieContainerRef.current,
      renderer: "svg",
      loop: true,
      autoplay: true,
      animationData,
      rendererSettings: {
        preserveAspectRatio: "xMidYMid meet",
      },
    });

    lottieAnimationRef.current.addEventListener("data_failed", () => {
      setIsLottiePreview(false);
      setValue("mediaFile", null, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: false,
      });
      setError("mediaFile", {
        type: "manual",
        message: t("splashScreen.add.validation.mediaInvalid"),
      });
    });
  };

  const validateLottie = (animation: Record<string, unknown>): boolean => {
    if (!animation || typeof animation !== "object") return false;
    if (
      animation.v == null ||
      animation.fr == null ||
      animation.ip == null ||
      animation.op == null ||
      animation.layers == null
    ) {
      return false;
    }

    const width = typeof animation.w === "number" ? animation.w : null;
    const height = typeof animation.h === "number" ? animation.h : null;

    if (width == null || height == null) return false;

    return isValidVerticalRatio(width, height);
  };

  useEffect(() => {
    return () => {
      destroyLottiePreview();
    };
  }, []);

  const validateImageDimensions = (file: File) =>
    new Promise<boolean>((resolve) => {
      const objectUrl = URL.createObjectURL(file);
      const image = new Image();

      image.onload = () => {
        const valid = isValidVerticalRatio(image.width, image.height);
        URL.revokeObjectURL(objectUrl);
        resolve(valid);
      };

      image.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(false);
      };

      image.src = objectUrl;
    });

  const validateVideoDimensions = (file: File) =>
    new Promise<boolean>((resolve) => {
      const objectUrl = URL.createObjectURL(file);
      const video = document.createElement("video");
      video.preload = "metadata";

      video.onloadedmetadata = () => {
        const valid = isValidVerticalRatio(video.videoWidth, video.videoHeight);
        URL.revokeObjectURL(objectUrl);
        resolve(valid);
      };

      video.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(false);
      };

      video.src = objectUrl;
    });

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;

    if (!file) {
      setValue("mediaFile", null, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
      clearPreviews();
      return;
    }

    const extension = file.name.split(".").pop()?.toLowerCase() || "";
    const isImage = ["jpg", "jpeg", "webp"].includes(extension);
    const isVideo = extension === "mp4";
    const isJson = extension === "json";

    if (!isImage && !isVideo && !isJson) {
      setValue("mediaFile", null, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: false,
      });
      setError("mediaFile", {
        type: "manual",
        message: t("splashScreen.add.validation.mediaInvalid"),
      });
      clearPreviews();
      return;
    }

    if (isImage && file.size > MAX_IMAGE_SIZE) {
      setValue("mediaFile", null, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: false,
      });
      setError("mediaFile", {
        type: "manual",
        message: t("splashScreen.add.validation.mediaInvalid"),
      });
      clearPreviews();
      return;
    }

    if (isVideo && file.size > MAX_VIDEO_SIZE) {
      setValue("mediaFile", null, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: false,
      });
      setError("mediaFile", {
        type: "manual",
        message: t("splashScreen.add.validation.mediaInvalid"),
      });
      clearPreviews();
      return;
    }

    if (isImage) {
      const validImageRatio = await validateImageDimensions(file);

      if (!validImageRatio) {
        setValue("mediaFile", null, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: false,
        });
        setError("mediaFile", {
          type: "manual",
          message: t("splashScreen.add.validation.mediaInvalid"),
        });
        clearPreviews();
        return;
      }
    }

    if (isVideo) {
      const validVideoRatio = await validateVideoDimensions(file);

      if (!validVideoRatio) {
        setValue("mediaFile", null, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: false,
        });
        setError("mediaFile", {
          type: "manual",
          message: t("splashScreen.add.validation.mediaInvalid"),
        });
        clearPreviews();
        return;
      }
    }

    if (isJson) {
      setMediaPreview(null);
      setVideoPreview(null);
      setIsLottiePreview(false);
      destroyLottiePreview();

      try {
        const text = await file.text();
        const lottieJson = JSON.parse(text) as Record<string, unknown>;

        if (!validateLottie(lottieJson)) {
          throw new Error("Invalid Lottie animation format");
        }

        setValue("mediaFile", file, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });

        setIsLottiePreview(true);
        try {
          renderLottiePreview(lottieJson);
        } catch (error) {
          void error;
          setIsLottiePreview(false);
          setValue("mediaFile", null, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: false,
          });
          setError("mediaFile", {
            type: "manual",
            message: t("splashScreen.add.validation.mediaInvalid"),
          });
        }

        return;
      } catch (error) {
        void error;
        setValue("mediaFile", null, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: false,
        });
        clearPreviews();
        setError("mediaFile", {
          type: "manual",
          message: t("splashScreen.add.validation.mediaInvalid"),
        });
        return;
      }
    }

    setValue("mediaFile", file, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });

    setIsLottiePreview(false);
    destroyLottiePreview();

    if (isImage) {
      setVideoPreview(null);
      const reader = new FileReader();
      reader.onload = () => setMediaPreview(reader.result as string);
      reader.readAsDataURL(file);
    } else if (isVideo) {
      setMediaPreview(null);
      setVideoPreview(URL.createObjectURL(file));
    } else {
      setMediaPreview(null);
      setVideoPreview(null);
    }
  };

  const HorizontalLine = (
    <Box
      sx={{
        borderTop: "1px solid var(--gray-100)",
        marginBottom: 3,
        marginTop: 3,
      }}
    />
  );

  const toBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () =>
        resolve((reader.result as string).split(",")[1] ?? "");
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const getMediaType = (file?: File | null) => {
    const extension = file?.name.split(".").pop()?.toLowerCase();

    switch (extension) {
      case "jpg":
      case "jpeg":
        return "IMAGE";
      case "webp":
        return "WEBP";
      case "mp4":
        return "VIDEO";
      case "json":
        return "LOTTIE";
      default:
        return null;
    }
  };

  const onSubmit: SubmitHandler<FormValues> = async (values) => {
    setLoading(true);
    try {
      const isLottie =
        values.mediaFile?.name.split(".").pop()?.toLowerCase() === "json";

      const splashHeaders = {
        authorization: tokenHeaders.headers.authorization,
        idToken: tokenHeaders.headers.idToken,
        countryCode: tokenHeaders.headers.countryCode,
      };
      const mediaType = getMediaType(values.mediaFile);
      const metadata = {
        name: values.name.trim(),
        countryCode: tokenHeaders.headers.countryCode,
        startDate: values.startDate,
        endDate: values.endDate,
        active: true,
        mediaType,
        displayDuration: null,
      } as Record<string, unknown>;

      if (isLottie && values.mediaFile) {
        const body = {
          ...metadata,
          fileBase64: await toBase64(values.mediaFile),
        };
        const requestConfig = {
          headers: splashHeaders,
        };
        await axios.post(`${CONFIG.API_URL}splash/lottie`, body, requestConfig);
      } else {
        const formData = new FormData();
        if (values.mediaFile) {
          formData.append("file", values.mediaFile);
        }
        formData.append("metadata", JSON.stringify(metadata));

        const headers = {
          ...splashHeaders,
          "Content-Type": "multipart/form-data",
        };
        await axios.post(`${CONFIG.API_URL}splash/contents`, formData, {
          headers,
        });
      }

      refreshPage?.();
      onClose();
      openSuccessAlert?.();
    } catch (error: any) {
      const apiErrorMessage = error?.response?.data?.message;
      openErrorAlert?.(apiErrorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <MyModal
      title={t("splashScreen.add.title")}
      modalChange={onClose}
      component="form"
      onSubmit={handleSubmit(onSubmit)}
    >
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 3 }} sx={styles.alignCenter}>
          <span style={styles.sectionLabel}>
            {t("splashScreen.add.identification")}
          </span>
        </Grid>
        <Grid size={{ xs: 12, md: 9 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormInputText
                id="name"
                type="text"
                label={t("splashScreen.add.name")}
                slotProps={{ htmlInput: { maxLength: 100, minLength: 1 } }}
                error={errors.name}
                {...register("name")}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {HorizontalLine}

      {showExpandedFields && (
        <>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 3 }} sx={styles.alignCenter}>
              <span style={styles.sectionLabel}>
                {t("splashScreen.add.period")}
              </span>
            </Grid>
            <Grid size={{ xs: 12, md: 9 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <FormInputDate
                      name="startDate"
                      control={control}
                      label={t("splashScreen.add.startDate")}
                      minDate={dayjs()}
                      error={errors.startDate}
                    />
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <FormInputDate
                      name="endDate"
                      control={control}
                      label={t("splashScreen.add.endDate")}
                      minDate={dayjs()}
                      error={errors.endDate}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {HorizontalLine}

          <Grid container spacing={2} sx={{ alignItems: "center" }}>
            <Grid size={{ xs: 12, md: 3 }}>
              <p style={{ ...styles.sectionLabel, margin: 0 }}>
                {t("splashScreen.add.media")}
              </p>
              <p style={{ ...styles.subLabel, margin: 0 }}>
                {t("splashScreen.add.mediaFormat")}
              </p>
              <p style={{ ...styles.subLabel, margin: 0 }}>
                {t("splashScreen.add.mediaTypes")}
              </p>
              <p style={{ ...styles.subLabel, margin: 0 }}>
                {t("splashScreen.add.mediaMaxSize")}
              </p>
            </Grid>

            <Grid
              size={{ xs: 12, md: 3 }}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                minHeight: 56,
              }}
            >
              <Button
                variant="contained"
                component="label"
                disabled={loading}
                onClick={() => fileInputRef.current?.click()}
              >
                {t("splashScreen.add.selectFile")}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/webp,video/mp4,application/json,.json"
                hidden
                onChange={handleFileChange}
              />
            </Grid>

            <Grid
              size={{ xs: 12, md: 6 }}
              sx={{ display: "flex", alignItems: "center", gap: 2 }}
            >
              <p style={styles.sectionLabel}>
                {t("splashScreen.add.currentMedia")}:
              </p>
              <div style={styles.imageContent}>
                {mediaPreview && (
                  <img
                    src={mediaPreview}
                    alt={t("splashScreen.add.currentMedia")}
                    style={styles.mediaPreview}
                  />
                )}
                {videoPreview && (
                  <video
                    src={videoPreview}
                    autoPlay
                    muted
                    loop
                    playsInline
                    style={styles.mediaPreview}
                  />
                )}
                <div
                  ref={lottieContainerRef}
                  style={{
                    ...styles.lottiePreview,
                    display: isLottiePreview ? "block" : "none",
                  }}
                />
              </div>
            </Grid>

            {errors.mediaFile?.message && (
              <Grid size={{ xs: 12, md: 9 }} offset={{ md: 3 }}>
                <Box sx={{ color: "var(--red-100)", fontSize: "14px" }}>
                  {String(errors.mediaFile.message)}
                </Box>
              </Grid>
            )}
          </Grid>

          {HorizontalLine}
        </>
      )}

      <p style={{ fontSize: 12, marginBottom: 30 }}>
        {t("splashScreen.add.requiredFields")}
      </p>

      {loading || isSubmitting ? (
        <CircularProgress />
      ) : (
        <ActionButtons
          typeButton="submit"
          saveName={t("splashScreen.add.saveButton")}
          disableSave={!isValid}
          enableLine={false}
        />
      )}
    </MyModal>
  );
}
